import { defineStore } from 'pinia'
import { AuthService, CookieService } from '@lingoking-node-apps/api'

import UserTypes from '@/enums/UserTypes'
import { Posthog } from '@lingoking-node-apps/analytics'

import { useRequestStateStore } from '@/stores/request-state'
import { useResetStore } from '@/composables/useResetStore'
import { useIntercom } from '@/composables/useIntercom'

import router from '@/router'

export type UserProfile = {
  id: string
  email: string
  firstName: string
  lastName: string
  userType: UserTypes
  companyPosition: any
  company: any
  guest: boolean
  newsletter: boolean
  favouriteJobMatchingLevel: boolean
  subscribeFeedbackEmail: boolean
  active: boolean
  confirmed: boolean
  enabled: boolean
  optOut: boolean
  language: string
  timezone: string
  userTitle: string
  billingAddress: {
    name: any
    id: string
    addressCountry: string
    addressLocality: string
    addressRegion: string
    postOfficeBoxNumber: string
    postalCode: string
    streetAddress: string
    addressSuffix: any
    houseNumber: string
  }
  telephone: string
  mobilePhone: any
  faxNumber: any
  profileImage: {
    contentUrl: string
    path: string
    name: string
    id: string
  }
  paymentData: any
  paymentMethod: any
  notificationEnabled: boolean
  createdAt: Date
}

type AuthStoreData = {
  loginError: any
  user: UserProfile | null
  admin: UserProfile | null
  returnUrl: string | null
}

const createDefaultStoreData = (): AuthStoreData => {
  return {
    loginError: null,
    user: null,
    admin: null,
    returnUrl: null
  }
}

export const useAuthStore = defineStore({
  id: 'auth',
  persist: true,
  state: (): AuthStoreData => createDefaultStoreData(),
  getters: {
    isPm: (state) => {
      return state.user && state.user.userType == UserTypes.TYPE_PM
    },
    isAdmin: (state) => {
      return state.user && state.user.userType == UserTypes.TYPE_ADMIN
    },
    requireProfileCompleted: (state) => {
      return state.user && state.user.userType === UserTypes.TYPE_CUSTOMER
    },
    hasSwitchUser: (state) => {
      const switchUser = CookieService.getSwitchUser()

      return !!state.admin && !!state.user && switchUser
    },
    pendingRegistration: (state) => {
      return (
        state.user &&
        (!state.user.userType ||
          !state.user.userTitle ||
          !state.user.userType ||
          !state.user.billingAddress)
      )
    }
  },
  actions: {
    async login(email: string, password: string) {
      const requestStateStore = useRequestStateStore()

      requestStateStore.loading = true

      try {
        await AuthService.login({ email: email, password: password })

        await this.profile()

        Posthog.capture.captureSuccessLogin({})

        router.push(this.returnUrl || '/')
      } catch (error: any) {
        requestStateStore.error = error
      } finally {
        requestStateStore.loading = false
      }
    },

    async logout() {
      const requestStateStore = useRequestStateStore()

      requestStateStore.loading = true

      try {
        await AuthService.logout()

        this.reset()
      } catch (error: any) {
        requestStateStore.error = error

        this.reset()
      } finally {
        requestStateStore.loading = false
      }
    },

    async refresh() {
      const requestStateStore = useRequestStateStore()

      requestStateStore.loading = true

      try {
        const response = await AuthService.refresh()

        return Promise.resolve(response)
      } catch (error: any) {
        requestStateStore.error = error

        this.reset()

        return Promise.reject(error)
      } finally {
        requestStateStore.loading = false
      }
    },

    async profile() {
      const requestStateStore = useRequestStateStore()

      requestStateStore.loading = true

      const response: { user: UserProfile; admin?: UserProfile } = await AuthService.profile()

      requestStateStore.loading = false

      this.user = response.user
      this.admin = response.admin || null

      this.identifyCustomer()
    },

    async setSwitchUser(email: string) {
      await CookieService.setSwitchUser(email)

      // load profile with impersonator header:
      await this.profile()

      router.push(this.returnUrl || '/')
    },

    async removeSwitchUser() {
      // clear cookies
      CookieService.removeAllCookies(false)

      // load profile without the impersonator header:
      await this.profile()

      this.returnUrl = null

      router.push('/select-customer')
    },

    identifyCustomer() {
      const intercom = useIntercom()

      if (!this.user) {
        return
      }

      if (this.isAdmin || this.isPm) {
        Posthog.user.setAdmin(this.user)
      } else if (this.admin) {
        Posthog.user.setAdmin(this.admin, this.user)
      } else {
        Posthog.user.setCustomer(this.user)
      }

      intercom.setCustomer(this.user)
    },

    reset(options: { redirectToLogin?: boolean } = { redirectToLogin: true }) {
      const resetStore = useResetStore()
      const intercom = useIntercom()

      // reset all stores:
      resetStore.all()

      Posthog.user.reset()
      intercom.reset()

      if (options.redirectToLogin) router.push('/login')
    }
  }
})
